import { getPrefs, subscribePrefs } from '../app/prefs';

/** Synthesized cabin sound: nothing is downloaded, every sound is built from noise and oscillators. */

const MUTE_KEY = 'f13-muted';
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

interface Beds {
  engineGain: GainNode;
  airGain: GainNode;
  engineFilter: BiquadFilterNode;
  hum: OscillatorNode;
  whine: OscillatorNode;
  whineGain: GainNode;
  stop: () => void;
}

class CabinAudio {
  muted = readMuted();
  private ctx: AudioContext | null = null;
  private out: GainNode | null = null;
  /** Everything passes through this low-pass, which a nearby blast slams shut for a few seconds. */
  private muffle: BiquadFilterNode | null = null;
  private white: AudioBuffer | null = null;
  private brown: AudioBuffer | null = null;
  private beds: Beds | null = null;
  private wantBeds = false;
  private engineLevel = 0.4;
  /** A room on the ground (the hotel, the gate hall): no engines, only quiet air. */
  private room = false;
  private readonly listeners = new Set<() => void>();

  /** Browsers only allow sound after a gesture; call this from pointer and key handlers. */
  unlock(): void {
    if (!this.ctx) {
      const Ctor = globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      const ctx = new Ctor();
      this.ctx = ctx;
      this.muffle = ctx.createBiquadFilter();
      this.muffle.type = 'lowpass';
      this.muffle.frequency.value = 20000;
      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.value = -14;
      compressor.ratio.value = 6;
      this.out = ctx.createGain();
      this.out.gain.value = this.level();
      this.muffle.connect(compressor).connect(this.out).connect(ctx.destination);
      this.white = this.noiseBuffer(2, false);
      this.brown = this.noiseBuffer(4, true);
      if (this.wantBeds) this.startBeds();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => {});
  }

  /** Engine drone and cabin air, for as long as you are in the 3D cabin. */
  start(): void {
    this.wantBeds = true;
    if (this.ctx && !this.beds) this.startBeds();
  }

  stop(): void {
    this.wantBeds = false;
    this.beds?.stop();
    this.beds = null;
  }

  /** 0 = engines idle, 0.45 = cruise, 1 = takeoff power (a little past it: reverse thrust, a hijacker's dive). */
  setEngine(level: number, seconds = 1.5): void {
    this.engineLevel = clamp(level, 0, 1.3);
    const b = this.beds;
    const ctx = this.ctx;
    if (!b || !ctx) return;
    const t = ctx.currentTime;
    const l = this.engineLevel;
    b.engineGain.gain.setTargetAtTime(this.room ? 0 : 0.1 + l * 0.34, t, seconds / 3);
    b.engineFilter.frequency.setTargetAtTime(160 + l * 520, t, seconds / 3);
    b.hum.frequency.setTargetAtTime(48 + l * 26, t, seconds / 3);
    b.whine.frequency.setTargetAtTime(2100 + l * 1700, t, seconds / 3);
    b.whineGain.gain.setTargetAtTime(this.room ? 0 : 0.0015 + l * 0.006, t, seconds / 3);
    b.airGain.gain.setTargetAtTime(this.room ? 0.004 : 0.012, t, seconds / 3);
  }

  /** On the ground indoors (the hotel room, the gate hall): the engines fall silent and the air goes quiet. */
  setRoomTone(on: boolean): void {
    if (this.room === on) return;
    this.room = on;
    this.setEngine(this.engineLevel, 0.8);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    try {
      localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
    } catch {
      // Private mode: the choice lasts for this visit only.
    }
    this.applyLevel();
    for (const fn of [...this.listeners]) fn();
  }

  /** How loud everything plays: the sound volume in your settings, unless muted. */
  private level(): number {
    return this.muted ? 0 : getPrefs().sound;
  }

  applyLevel(): void {
    if (this.ctx && this.out) this.out.gain.setTargetAtTime(this.level(), this.ctx.currentTime, 0.05);
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  }

  /** The seatbelt sign: one soft high bing. */
  chime(): void {
    const t = this.now();
    if (t === null) return;
    this.bell(988, t, 1.6, 0.2);
  }

  /** Before the captain speaks: ding-dong. */
  ding(): void {
    const t = this.now();
    if (t === null) return;
    this.bell(1175, t, 1.2, 0.17);
    this.bell(880, t + 0.42, 1.5, 0.17);
  }

  /** The cabin lights relay switching. */
  clunk(): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 0.035, { type: 'bandpass', frequency: 1800, q: 1.2 }, 0.35, 0.002);
    this.tone(t, 'sine', 95, 60, 0.14, 0.28);
  }

  /** Landing gear folding away. */
  thunk(): void {
    const t = this.now();
    if (t === null) return;
    this.tone(t, 'sine', 70, 38, 0.3, 0.5);
    this.noise(t, 0.2, { type: 'lowpass', frequency: 320 }, 0.5, 0.005, true);
  }

  /** A bomb going off `distance` metres away. */
  boom(distance: number): void {
    const ctx = this.ctx;
    const t = this.now();
    if (!ctx || t === null || !this.muffle) return;
    const near = clamp(1 - distance / 14, 0.18, 1);
    // Crack, body, sub thump.
    this.noise(t, 0.3, { type: 'lowpass', frequency: 7000, to: 700 }, 0.9 * near, 0.002);
    this.noise(t, 3, { type: 'lowpass', frequency: 1200, to: 70 }, 1.1 * near, 0.01, true);
    this.tone(t, 'sine', 75, 26, 0.9, 1.0 * near);
    // Debris rattling down.
    for (let i = 0; i < 14; i++) {
      const at = t + 0.25 + Math.random() * 1.6;
      this.noise(at, 0.02 + Math.random() * 0.03, { type: 'highpass', frequency: 1500 + Math.random() * 3000 }, 0.12 * near * Math.random(), 0.001);
    }
    // Close by, your ears ring and everything sounds far away for a few seconds.
    if (near > 0.55) {
      this.tone(t + 0.15, 'sine', 3900, 3900, 3.5, 0.035 * near);
      this.muffle.frequency.cancelScheduledValues(t);
      this.muffle.frequency.setValueAtTime(20000, t);
      this.muffle.frequency.exponentialRampToValueAtTime(260, t + 0.12);
      this.muffle.frequency.setValueAtTime(260, t + 1.2);
      this.muffle.frequency.exponentialRampToValueAtTime(20000, t + 4.5);
    }
  }

  /** A turbulence bump: a low shudder, a creak and the bins rattling. */
  rumble(strength = 1): void {
    const t = this.now();
    if (t === null) return;
    const s = clamp(strength, 0.2, 1.5);
    this.noise(t, 1.3, { type: 'lowpass', frequency: 140 }, 0.9 * s, 0.05, true);
    this.tone(t + 0.1, 'sawtooth', 44, 40, 0.45, 0.03 * s, { type: 'bandpass', frequency: 650, q: 7 });
    for (let i = 0; i < 6; i++) {
      this.noise(t + 0.05 + Math.random() * 0.8, 0.015, { type: 'highpass', frequency: 2500 }, 0.1 * s, 0.001);
    }
  }

  /** The drink cart rolling loose: wheels and clinking cans. */
  rattle(): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 2.4, { type: 'bandpass', frequency: 260, q: 0.8 }, 0.35, 0.4, true);
    for (let i = 0; i < 16; i++) {
      const at = t + Math.random() * 2.2;
      this.tone(at, 'sine', 2000 + Math.random() * 2200, 2000, 0.07, 0.05);
    }
  }

  /** A footstep on the aisle carpet. */
  step(): void {
    const t = this.now();
    if (t === null) return;
    const v = 0.8 + Math.random() * 0.4;
    this.noise(t, 0.09, { type: 'lowpass', frequency: 380 * v }, 0.32 * v, 0.004, true);
    this.noise(t + 0.01, 0.03, { type: 'bandpass', frequency: 2200 * v, q: 1.5 }, 0.05, 0.002);
  }

  /** Clothes against the seat as you stand up or sit down. */
  rustle(): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 0.45, { type: 'bandpass', frequency: 1900, to: 1200, q: 0.9 }, 0.09, 0.08);
    this.noise(t + 0.05, 0.3, { type: 'lowpass', frequency: 260 }, 0.18, 0.03, true);
  }

  /** The click of a phone flashlight. */
  click(): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 0.012, { type: 'highpass', frequency: 3500 }, 0.25, 0.001);
    this.tone(t, 'square', 1900, 1900, 0.02, 0.02);
  }

  /** A bomb's fuse ticking down: `urgency` 0..1 raises the pitch. */
  beep(urgency: number): void {
    const t = this.now();
    if (t === null) return;
    const u = clamp(urgency, 0, 1);
    this.tone(t, 'square', 2300 + u * 900, 2300 + u * 900, 0.07, 0.035 + u * 0.03, { type: 'lowpass', frequency: 6000 });
  }

  /** Something thrown past you through the air. */
  whoosh(): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 0.4, { type: 'bandpass', frequency: 380, to: 1500, q: 1.1 }, 0.16, 0.12);
  }

  /** Something heavy landing on a soft bed (`weight` 0..1; more for a door flung open, up to 2.2). */
  thud(weight = 1): void {
    const t = this.now();
    if (t === null) return;
    const w = clamp(weight, 0.1, 2.2);
    this.tone(t, 'sine', 95, 52, 0.28, 0.42 * w);
    this.noise(t, 0.22, { type: 'lowpass', frequency: 300 }, 0.5 * w, 0.004, true);
    this.noise(t, 0.08, { type: 'bandpass', frequency: 900, q: 0.8 }, 0.06 * w, 0.003);
  }

  /** A suitcase zipper run over `seconds`. */
  zipper(seconds = 0.9): void {
    const ctx = this.ctx;
    const t = this.now();
    if (!ctx || t === null || !this.white || !this.muffle) return;
    const src = ctx.createBufferSource();
    src.buffer = this.white;
    src.loop = true;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 2.2;
    band.frequency.setValueAtTime(1500, t);
    band.frequency.linearRampToValueAtTime(2600, t + seconds);
    const gate = ctx.createGain();
    gate.gain.value = 0;
    const teeth = ctx.createOscillator();
    teeth.type = 'square';
    teeth.frequency.setValueAtTime(55, t);
    teeth.frequency.linearRampToValueAtTime(75, t + seconds);
    const depth = ctx.createGain();
    depth.gain.value = 0.16;
    teeth.connect(depth).connect(gate.gain);
    src.connect(band).connect(gate).connect(this.muffle);
    src.start(t);
    src.stop(t + seconds);
    teeth.start(t);
    teeth.stop(t + seconds);
  }

  /** An item dropped into a padded pocket. */
  pop(): void {
    const t = this.now();
    if (t === null) return;
    this.tone(t, 'sine', 560, 330, 0.09, 0.1);
    this.noise(t, 0.05, { type: 'lowpass', frequency: 900 }, 0.18, 0.002, true);
  }

  /** A zip tie being pulled tight. */
  zip(): void {
    const ctx = this.ctx;
    const t = this.now();
    if (!ctx || t === null || !this.white || !this.muffle) return;
    const src = ctx.createBufferSource();
    src.buffer = this.white;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 3;
    band.frequency.setValueAtTime(1300, t);
    band.frequency.exponentialRampToValueAtTime(3600, t + 0.35);
    const gate = ctx.createGain();
    gate.gain.value = 0;
    const saw = ctx.createOscillator();
    saw.type = 'sawtooth';
    saw.frequency.setValueAtTime(45, t);
    saw.frequency.linearRampToValueAtTime(85, t + 0.35);
    const depth = ctx.createGain();
    depth.gain.value = 0.25;
    saw.connect(depth).connect(gate.gain);
    src.connect(band).connect(gate).connect(this.muffle);
    src.start(t);
    src.stop(t + 0.38);
    saw.start(t);
    saw.stop(t + 0.38);
  }

  /** Wire cutters through the right wire: a snip, and the device powering down. */
  snip(): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 0.03, { type: 'highpass', frequency: 3000 }, 0.3, 0.001);
    this.tone(t + 0.01, 'square', 1500, 1500, 0.025, 0.025);
    this.tone(t + 0.25, 'sine', 880, 110, 0.55, 0.06);
  }

  /** Someone taken ill: two coughs and a ragged breath (`gain` 0..1 by distance). */
  cough(gain = 1): void {
    const t = this.now();
    if (t === null) return;
    const g = clamp(gain, 0.1, 1);
    for (const at of [0, 0.34]) {
      this.noise(t + at, 0.14, { type: 'bandpass', frequency: 620, q: 1.4 }, 0.28 * g, 0.004, true);
      this.noise(t + at, 0.1, { type: 'bandpass', frequency: 1900, q: 1 }, 0.05 * g, 0.004);
    }
    this.noise(t + 0.75, 0.55, { type: 'bandpass', frequency: 1300, to: 800, q: 0.8 }, 0.05 * g, 0.2);
  }

  /** A soft tick: the clock in its last seconds, a button, a vote. `bright` for your own. */
  tick(bright = false): void {
    const t = this.now();
    if (t === null) return;
    this.tone(t, 'triangle', bright ? 1760 : 1320, bright ? 1760 : 1320, 0.05, bright ? 0.05 : 0.03);
    this.noise(t, 0.01, { type: 'highpass', frequency: 5000 }, 0.02, 0.0005);
  }

  /** Thunder far off: a slow roll with no rattling bins (turbulence has those). */
  thunder(near = 0.5): void {
    const t = this.now();
    if (t === null) return;
    const n = clamp(near, 0.1, 1);
    this.noise(t, 3.4, { type: 'lowpass', frequency: 260, to: 55 }, 0.55 * n, 0.35, true);
    this.noise(t + 0.04, 0.3, { type: 'lowpass', frequency: 1100 }, 0.12 * n, 0.02, true);
  }

  /** A door: the latch, then (opening) the swing, or (closing) the thump as it shuts. */
  door(open: boolean): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 0.022, { type: 'bandpass', frequency: 2600, q: 3 }, 0.22, 0.001);
    this.tone(t, 'sine', open ? 320 : 190, open ? 240 : 140, 0.09, 0.05);
    if (open) this.noise(t + 0.08, 0.5, { type: 'bandpass', frequency: 420, q: 0.9 }, 0.05, 0.15, true);
    else this.noise(t + 0.02, 0.18, { type: 'lowpass', frequency: 240 }, 0.3, 0.004, true);
  }

  /** Oxygen masks: the panels popping open and rubber cups clattering down. */
  masks(): void {
    const t = this.now();
    if (t === null) return;
    for (let i = 0; i < 12; i++) {
      const at = t + Math.random() * 0.5;
      this.noise(at, 0.03, { type: 'bandpass', frequency: 900 + Math.random() * 900, q: 2 }, 0.1, 0.001);
      this.tone(at + 0.04, 'sine', 320, 190, 0.07, 0.025);
    }
  }

  /** The cart's wheels rolling for `seconds` (a can or two clinking), `level` 0..1. */
  roll(seconds: number, level = 1): void {
    const t = this.now();
    if (t === null) return;
    const l = clamp(level, 0.1, 1);
    this.noise(t, seconds, { type: 'bandpass', frequency: 230, q: 0.9 }, 0.14 * l, Math.min(0.4, seconds / 3), true);
    for (let i = 0; i < Math.round(seconds * 1.5); i++) {
      const f = 2300 + Math.random() * 1400;
      this.tone(t + Math.random() * seconds, 'sine', f, f, 0.06, 0.018 * l);
    }
  }

  /** Something for you alone (a whisper, your team): two soft notes. */
  ping(): void {
    const t = this.now();
    if (t === null) return;
    this.bell(1568, t, 0.5, 0.05);
    this.bell(2093, t + 0.09, 0.6, 0.04);
  }

  /** Something wrong right under you: a low sting. */
  sting(): void {
    const t = this.now();
    if (t === null) return;
    this.tone(t, 'sawtooth', 110, 98, 1.6, 0.045, { type: 'lowpass', frequency: 900 });
    this.tone(t, 'sine', 55, 49, 1.8, 0.12);
    this.tone(t + 0.02, 'triangle', 233, 220, 1.3, 0.03);
  }

  /** Your own heart, slowing to a stop. */
  heartbeat(): void {
    const t = this.now();
    if (t === null) return;
    let at = t;
    for (let i = 0; i < 4; i++) {
      const k = 1 - i * 0.22;
      this.tone(at, 'sine', 62, 40, 0.13, 0.4 * k);
      this.tone(at + 0.19, 'sine', 56, 38, 0.1, 0.25 * k);
      at += 0.85 + i * 0.3;
    }
  }

  /** Rising as a ghost: an airy swell. */
  swell(): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 2.8, { type: 'bandpass', frequency: 500, to: 2400, q: 0.7 }, 0.07, 1.2);
    this.tone(t, 'sine', 440, 660, 2.6, 0.018);
  }

  /** The runway under the wheels for `seconds`: rumble and the thump of the joints, faster as you go. */
  runway(seconds: number): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, seconds, { type: 'lowpass', frequency: 170 }, 0.3, seconds * 0.4, true);
    let at = t + 0.4;
    let gap = 0.7;
    while (at < t + seconds - 0.3) {
      this.noise(at, 0.06, { type: 'lowpass', frequency: 220 }, 0.16, 0.002, true);
      at += gap;
      gap = Math.max(0.12, gap * 0.86);
    }
  }

  /** Tyres touching down. */
  squeal(): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 0.35, { type: 'bandpass', frequency: 1700, to: 1100, q: 4 }, 0.1, 0.01);
    this.noise(t + 0.02, 0.45, { type: 'lowpass', frequency: 200 }, 0.5, 0.01, true);
  }

  /** The master caution: a two-tone alarm, `times` over. */
  alarm(times = 4): void {
    const t = this.now();
    if (t === null) return;
    for (let i = 0; i < times; i++) {
      this.tone(t + i * 0.62, 'square', 880, 880, 0.26, 0.03, { type: 'lowpass', frequency: 3000 });
      this.tone(t + i * 0.62 + 0.29, 'square', 660, 660, 0.26, 0.03, { type: 'lowpass', frequency: 3000 });
    }
  }

  /** Wind roaring through the cabin for `seconds`. */
  wind(seconds: number): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, seconds, { type: 'bandpass', frequency: 420, to: 900, q: 0.6 }, 0.35, 1.0);
    this.noise(t, seconds, { type: 'lowpass', frequency: 160 }, 0.45, 1.2, true);
  }

  /** A bobby pin in a lock: small metal clicks, then the cuffs spring open. */
  lockpick(): void {
    const t = this.now();
    if (t === null) return;
    for (let i = 0; i < 6; i++) this.noise(t + i * 0.13 + Math.random() * 0.05, 0.012, { type: 'highpass', frequency: 4000 }, 0.12, 0.001);
    this.noise(t + 0.95, 0.05, { type: 'bandpass', frequency: 2500, q: 2 }, 0.22, 0.001);
    this.tone(t + 0.95, 'square', 1200, 900, 0.05, 0.03);
  }

  /** Handcuffs clicking shut. */
  cuff(): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 0.015, { type: 'highpass', frequency: 3500 }, 0.25, 0.001);
    this.noise(t + 0.07, 0.02, { type: 'highpass', frequency: 3000 }, 0.2, 0.001);
    this.tone(t, 'square', 2600, 2600, 0.02, 0.02);
  }

  /** Cutlery and china. */
  clink(): void {
    const t = this.now();
    if (t === null) return;
    for (let i = 0; i < 4; i++) {
      const f = 2800 + Math.random() * 1400;
      this.tone(t + i * 0.09 + Math.random() * 0.05, 'sine', f, f, 0.14, 0.03);
    }
  }

  /** Someone clapping (`gain` by distance). */
  clap(gain = 1): void {
    const t = this.now();
    if (t === null) return;
    const g = clamp(gain, 0.05, 1);
    for (let i = 0; i < 5; i++) this.noise(t + i * 0.24 + Math.random() * 0.03, 0.05, { type: 'bandpass', frequency: 1400, q: 1.2 }, 0.16 * g, 0.001);
  }

  /** The gate's scanner: reading, then a bright double beep once it takes the pass. */
  scan(ok: boolean): void {
    const t = this.now();
    if (t === null) return;
    this.tone(t, 'sine', 1760, 1760, 0.07, 0.05);
    if (ok) this.tone(t + 0.1, 'sine', 2350, 2350, 0.12, 0.06);
  }

  /** A finger on paper, or a card turned over. */
  paper(): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 0.07, { type: 'bandpass', frequency: 3200, q: 1 }, 0.035, 0.003);
  }

  /** That will not work: a short low blip. */
  nope(): void {
    const t = this.now();
    if (t === null) return;
    this.tone(t, 'square', 330, 250, 0.12, 0.025, { type: 'lowpass', frequency: 1500 });
  }

  /** A switch or a dial detent on the flight deck. */
  switchClick(): void {
    const t = this.now();
    if (t === null) return;
    this.noise(t, 0.01, { type: 'highpass', frequency: 3000 }, 0.18, 0.0005);
    this.tone(t, 'square', 3000, 3000, 0.01, 0.012);
  }

  private startBeds(): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const engineSrc = ctx.createBufferSource();
    engineSrc.buffer = this.brown;
    engineSrc.loop = true;
    const engineFilter = ctx.createBiquadFilter();
    engineFilter.type = 'lowpass';
    engineFilter.Q.value = 0.6;
    const engineGain = ctx.createGain();
    engineGain.gain.value = 0;
    engineSrc.connect(engineFilter).connect(engineGain).connect(this.muffle!);

    const hum = ctx.createOscillator();
    hum.type = 'sawtooth';
    const humFilter = ctx.createBiquadFilter();
    humFilter.type = 'lowpass';
    humFilter.frequency.value = 180;
    const humGain = ctx.createGain();
    humGain.gain.value = 0.025;
    hum.connect(humFilter).connect(humGain).connect(engineGain);

    const whine = ctx.createOscillator();
    whine.type = 'sine';
    const whineGain = ctx.createGain();
    whineGain.gain.value = 0;
    whine.connect(whineGain).connect(this.muffle!);

    // A slow swell so the drone never sounds like a loop.
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.11;
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = 0.03;
    lfo.connect(lfoDepth).connect(engineGain.gain);

    const airSrc = ctx.createBufferSource();
    airSrc.buffer = this.white;
    airSrc.loop = true;
    const airHigh = ctx.createBiquadFilter();
    airHigh.type = 'highpass';
    airHigh.frequency.value = 2200;
    const airLow = ctx.createBiquadFilter();
    airLow.type = 'lowpass';
    airLow.frequency.value = 8000;
    const airGain = ctx.createGain();
    airGain.gain.value = 0;
    airGain.gain.setTargetAtTime(0.012, t, 1);
    airSrc.connect(airHigh).connect(airLow).connect(airGain).connect(this.muffle!);

    for (const node of [engineSrc, hum, whine, lfo, airSrc]) node.start(t);
    this.beds = {
      engineGain,
      airGain,
      engineFilter,
      hum,
      whine,
      whineGain,
      stop: () => {
        const now = ctx.currentTime;
        engineGain.gain.setTargetAtTime(0, now, 0.3);
        airGain.gain.setTargetAtTime(0, now, 0.3);
        whineGain.gain.setTargetAtTime(0, now, 0.3);
        for (const node of [engineSrc, hum, whine, lfo, airSrc]) node.stop(now + 1.5);
      },
    };
    this.setEngine(this.engineLevel, 2);
  }

  /**
   * A bot talking: blips of gibberish (see babble.ts), from its side of the cabin (`pan` -1..1) and as loud as
   * its distance allows (`gain` 0..1).
   */
  babble(syllables: readonly { at: number; dur: number; freq: number; formant: number; level: number }[], opts: { gain: number; pan: number }): void {
    const t = this.now();
    if (t === null || opts.gain < 0.02 || syllables.length === 0) return;
    const ctx = this.ctx!;
    const pan = ctx.createStereoPanner();
    pan.pan.value = clamp(opts.pan, -1, 1);
    const bus = ctx.createGain();
    bus.gain.value = 0.2 * opts.gain;
    bus.connect(pan).connect(this.muffle!);
    let end = t;
    for (const s of syllables) {
      const at = t + 0.03 + s.at;
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.setValueAtTime(s.freq, at);
      osc.frequency.linearRampToValueAtTime(s.freq * 0.9, at + s.dur);
      // Two formants: a fixed low one for body, the vowel's for colour.
      const body = ctx.createBiquadFilter();
      body.type = 'bandpass';
      body.frequency.value = 600;
      body.Q.value = 3;
      const vowel = ctx.createBiquadFilter();
      vowel.type = 'bandpass';
      vowel.frequency.value = s.formant;
      vowel.Q.value = 4;
      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, at);
      env.gain.exponentialRampToValueAtTime(Math.max(0.0002, s.level), at + 0.012);
      env.gain.exponentialRampToValueAtTime(0.0001, at + s.dur);
      osc.connect(body).connect(env);
      osc.connect(vowel).connect(env);
      env.connect(bus);
      osc.start(at);
      osc.stop(at + s.dur + 0.03);
      end = Math.max(end, at + s.dur + 0.05);
    }
    setTimeout(() => {
      bus.disconnect();
      pan.disconnect();
    }, (end - t) * 1000 + 200);
  }

  private now(): number | null {
    return this.ctx && this.muffle ? this.ctx.currentTime : null;
  }

  private noiseBuffer(seconds: number, brown: boolean): AudioBuffer {
    const ctx = this.ctx!;
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      if (brown) {
        last = (last + 0.02 * white) / 1.02;
        data[i] = last * 3.5;
      } else {
        data[i] = white;
      }
    }
    return buffer;
  }

  /** A bell-like tone with a couple of inharmonic partials. */
  private bell(frequency: number, at: number, seconds: number, level: number): void {
    for (const [multiple, amount] of [
      [1, 1],
      [2.76, 0.16],
      [5.4, 0.05],
    ] as const) {
      this.tone(at, 'sine', frequency * multiple, frequency * multiple, seconds / multiple, level * amount);
    }
  }

  private tone(
    at: number,
    type: OscillatorType,
    from: number,
    to: number,
    seconds: number,
    level: number,
    filter?: { type: BiquadFilterType; frequency: number; q?: number },
  ): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(from, at);
    if (to !== from) osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), at + seconds);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, level), at + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + seconds);
    let node: AudioNode = osc;
    if (filter) {
      const f = ctx.createBiquadFilter();
      f.type = filter.type;
      f.frequency.value = filter.frequency;
      if (filter.q) f.Q.value = filter.q;
      node = node.connect(f);
    }
    node.connect(gain).connect(this.muffle!);
    osc.start(at);
    osc.stop(at + seconds + 0.05);
  }

  private noise(
    at: number,
    seconds: number,
    filter: { type: BiquadFilterType; frequency: number; to?: number; q?: number },
    level: number,
    attack: number,
    brown = false,
  ): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = brown ? this.brown : this.white;
    const f = ctx.createBiquadFilter();
    f.type = filter.type;
    f.frequency.setValueAtTime(filter.frequency, at);
    if (filter.to) f.frequency.exponentialRampToValueAtTime(filter.to, at + seconds);
    if (filter.q) f.Q.value = filter.q;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, level), at + Math.max(0.001, attack));
    gain.gain.exponentialRampToValueAtTime(0.0001, at + seconds);
    src.connect(f).connect(gain).connect(this.muffle!);
    src.start(at, Math.random() * 1.5);
    src.stop(at + seconds + 0.05);
  }
}

export const cabinAudio = new CabinAudio();
subscribePrefs(() => cabinAudio.applyLevel());
